import React from 'react';

import { CustomerGroup } from '../../../home/types/home.types';
import { DEFAULT_PROMO_LABEL } from '../../constants/cartConstants';
import { CartGroupView, CartMetadataItem, AddOnConfigData, AvailableAddOnsShape } from '../../types/cartTypes';
import { getProductName, formatCurrency } from '../../utils/cartFormatting';
import { getDiscountObj } from '../../utils/cartNormalization';
import { getDelegateRateToggles } from '../../utils/cartPageHelpers';
import BasketItem from '../basket/basketItem';



interface CartItemRowProps {
  group: CartGroupView;
  productMetadata: Record<string, CartMetadataItem>;
  availableAddOns: AvailableAddOnsShape;
  customerGroups: CustomerGroup[];
  config: { locale: string };
  currency: string;
  onIncrement: (group: CartGroupView) => void;
  onDecrement: (group: CartGroupView) => void;
  onConfigureAddOns: (group: CartGroupView, data: AddOnConfigData) => Promise<void>;
  locale: string;
}

const CartItemRow: React.FC<CartItemRowProps> = ({
  group,
  productMetadata,
  availableAddOns,
  customerGroups,
  config,
  currency,
  onIncrement,
  onDecrement,
  onConfigureAddOns,
  locale,
}) => {
  const lineItem = group.mainItem;
  if (!lineItem) return null;

  const allocatedAddOns = group.addOns.map((addonItem) => {
    const productName = getProductName(addonItem, config.locale);
    let unitPrice = 0;
    if (typeof addonItem.price === 'number') {
      unitPrice = addonItem.price / 100;
    } else if (addonItem.price && typeof addonItem.price === 'object' && 'value' in addonItem.price) {
      const priceVal = addonItem.price as { value: { centAmount: number } };
      if (priceVal.value?.centAmount != null) {
        unitPrice = priceVal.value.centAmount / 100;
      }
    }
    return {
      id: addonItem.productId,
      name: productName,
      price: unitPrice,
    };
  });

  const productAddOnIds = (availableAddOns.productAddOnsMap[lineItem.productId]) ?? [];
  const { products } = availableAddOns;
  const lineItemAvailableAddOns = products.filter((addon: { id: string }) => productAddOnIds.includes(addon.id));

  const productName = getProductName(lineItem, config.locale);
  const metadata = (productMetadata[lineItem.productId]) ?? {};

  const shouldShowDelegateLabel =
    group.showDelegateRateLabel &&
    (lineItem.custom?.fields?.['delegateRate'] as string)?.trim() !== '';

  const startVal = (metadata.startDate as string) || '';
  const endVal = (metadata.endDate as string) || '';

  const venueVal = (metadata.venue || metadata.location) ? (metadata.venue as string) : '';
  const locationVal = (metadata.venue || metadata.location) ? (metadata.location as string) : '';

  const itemPrice = (lineItem.price || 0) / 100;
  const itemOriginalPrice: number | null = metadata.originalPrice || null;

  const hasDiscount = itemOriginalPrice != null && itemPrice < itemOriginalPrice;
  
  let promoLabel: string | null = null;
  let promoValue: string | null = null;

  // For simplified GroupedMainItem, price is a number. 
  // We check metadata for discount details or use hasDiscount flag.
  const discountObj = getDiscountObj(lineItem.price);

  if (discountObj) {
    promoLabel = DEFAULT_PROMO_LABEL;

    const discValue = discountObj.value;
    if (discValue?.type === 'absolute' && Array.isArray(discValue.money) && discValue.money.length > 0) {
      const moneyArr = discValue.money;
      const moneyVal = moneyArr.find((m) => m.currencyCode === currency) || moneyArr[0];
      if (moneyVal?.centAmount != null) {
        promoValue = `-${formatCurrency(moneyVal.centAmount / 100, currency, locale)}`;
      }
    }
  }

  if (!promoLabel && (hasDiscount || metadata.discountName)) {
    promoLabel = DEFAULT_PROMO_LABEL;
  }

  if (!promoValue) {
    let derivedSaving = 0;
    const prices = (metadata.prices ?? []) as Array<{ value?: { centAmount: number }; discounted?: { value?: { centAmount: number } } }>;
    if (prices?.length) {
      const matchingPrice = prices.find((p) => {
        const pValue = (p.value?.centAmount ?? 0) / 100;
        const pDiscounted = p.discounted ? (p.discounted.value?.centAmount ?? 0) / 100 : null;
        return Math.abs(pValue - itemPrice) < 0.01 || (pDiscounted != null && Math.abs(pDiscounted - itemPrice) < 0.01);
      });
      if (matchingPrice) {
        const pValue = (matchingPrice.value?.centAmount ?? 0) / 100;
        const pDiscounted = matchingPrice.discounted ? (matchingPrice.discounted.value?.centAmount ?? 0) / 100 : null;
        if (pDiscounted != null && pValue > pDiscounted) {
          derivedSaving = pValue - pDiscounted;
        }
      }
    }
    if (derivedSaving === 0 && hasDiscount && itemOriginalPrice != null) {
      derivedSaving = itemOriginalPrice - itemPrice;
    }
    if (derivedSaving > 0) {
      promoValue = `-${formatCurrency(derivedSaving, currency, locale)}`;
      if (!promoLabel) promoLabel = DEFAULT_PROMO_LABEL;
    }
  }

  const toggles = getDelegateRateToggles(String(lineItem.custom?.fields?.['delegateRate'] ?? ''), customerGroups, config.locale);

  return (
    <div className={group.isLastInDelegateRate && group.assocKey ? 'mb-6' : ''}>
      {shouldShowDelegateLabel && (
        <div className="text-xs font-normal text-[#667085] leading-[1.5] tracking-[0px] mb-4 md:mb-6 mt-4 first:mt-0">
          Attendee Type: {lineItem.custom?.fields?.['delegateRate'] as string}
        </div>
      )}
      <BasketItem
        title={productName}
        addOns={allocatedAddOns}
        price={itemPrice}
        originalPrice={itemOriginalPrice ?? undefined}
        quantity={lineItem.quantity ?? 0}
        currency={currency}
        locale={locale}
        promoLabel={promoLabel ?? undefined}
        promoValue={promoValue ?? undefined}
        onQuantityChange={(qty: number) => {
          if (qty > (lineItem.quantity ?? 0)) {
            onIncrement(group);
          } else if (qty < (lineItem.quantity ?? 0)) {
            onDecrement(group);
          }
        }}
        onRemove={() => onDecrement(group)}
        startDate={startVal as string | undefined}
        endDate={endVal as string | undefined}
        venue={venueVal}
        location={locationVal}
        rawOptions={(metadata.rawOptions ?? [])}
        rawAddOns={lineItemAvailableAddOns}
        selectedOption={(() => {
          const optionsList = lineItem.custom?.fields?.['selectedOptionsList'] as string[] | undefined;
          const val = metadata.selectedSession ?? optionsList?.[0];
          return val != null ? String(val) : null;
        })()}
        selectedOptions={((() => {
          const optionsList = lineItem.custom?.fields?.['selectedOptionsList'] as string[] | undefined;
          if (Array.isArray(optionsList) && optionsList.length > 0) return optionsList;
          const legacy = metadata.selectedSession;
          return legacy ? [legacy] : [];
        })())}
        isMultiOption={(metadata.isMultiOption as boolean) ?? false}
        lineItemId={lineItem.id}
        productId={lineItem.productId}
        attachedAddOnIds={group.addOns.map((a) => a.productId)}
        onConfigureAddOns={(data) => onConfigureAddOns(group, data)}
        showAddOns={toggles.showAddOns}
        showOptions={toggles.showOptions}
      >
        {null}
      </BasketItem>
    </div>
  );
};

export default CartItemRow;
